import React, { useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Bell, Loader2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { hrmsPut } from "../../api/api";
import sessionDB from "../../utils/sessionDB";

const fieldClass = "mt-1 w-full rounded-lg border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/20";

const optionsByField = {
  theme: ["light", "dark"],
  uioption: ["horizontalbar", "sidebar"],
};

function labelFor(key) {
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());
}

function fieldsFrom(value, path = []) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value).flatMap(([key, child]) => {
    const childPath = [...path, key];
    if (child && typeof child === "object" && !Array.isArray(child)) {
      return fieldsFrom(child, childPath);
    }
    return [{ path: childPath, key, value: child }];
  });
}

function replaceAtPath(source, path, value) {
  const next = { ...source };
  let cursor = next;
  path.forEach((part, index) => {
    if (index === path.length - 1) cursor[part] = value;
    else {
      cursor[part] = { ...cursor[part] };
      cursor = cursor[part];
    }
  });
  return next;
}

export function UserPreferencesModal({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const initialPreferencesRef = useRef({});
  const fields = useMemo(() => fieldsFrom(preferences), [preferences]);

  useEffect(() => {
    if (!open) return;
    const storedPreferences = sessionDB.getEmpPreferences();
    initialPreferencesRef.current = storedPreferences;
    setPreferences(storedPreferences);
    setMessage(null);
  }, [open]);

  function updateField(field, event) {
    let value;
    if (typeof field.value === "boolean") value = event.target.checked;
    else if (typeof field.value === "number") value = event.target.value === "" ? "" : Number(event.target.value);
    else if (Array.isArray(field.value)) {
      try {
        value = JSON.parse(event.target.value);
        if (!Array.isArray(value)) throw new Error();
        setMessage(null);
      } catch {
        setMessage({ type: "error", text: `${labelFor(field.key)} must be a valid JSON array.` });
        return;
      }
    } else value = event.target.value;
    setPreferences((current) => replaceAtPath(current, field.path, value));
  }

  async function handleSave(event) {
    event.preventDefault();
    const hasChanges = JSON.stringify(preferences) !== JSON.stringify(initialPreferencesRef.current);
    if (!hasChanges) {
      onOpenChange(false);
      return;
    }

    const empId = sessionDB.getEmpObj().emp_id;
    if (empId === undefined || empId === null || empId === "") {
      setMessage({ type: "error", text: "Employee ID is missing from the browser session." });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      await hrmsPut(`/emp-info/update-user-pref/${encodeURIComponent(empId)}`, {
        empPreferences: preferences,
      });
      sessionDB.updateEmpPreferences(preferences);
      window.dispatchEvent(new CustomEvent("emp-preferences-updated", { detail: preferences }));
      onOpenChange(false);
      window.setTimeout(() => window.location.reload(), 0);
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Unable to save user preferences." });
    } finally {
      setSaving(false);
    }
  }

  function handleChangePassword() {
    onOpenChange(false);
    navigate("/hrms-ess/change-password");
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-slate-950/55" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white shadow-2xl focus:outline-none">
          <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-4">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)]"><Bell className="h-4 w-4" /></span>
              <div>
                <DialogPrimitive.Title className="font-semibold text-[var(--color-text-primary)]">User Preferences</DialogPrimitive.Title>
                <DialogPrimitive.Description className="text-sm text-[var(--color-text-secondary)]">Choose your preferred settings</DialogPrimitive.Description>
              </div>
            </div>
            <DialogPrimitive.Close className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Close"><X className="h-4 w-4" /></DialogPrimitive.Close>
          </div>

          <form onSubmit={handleSave} className="space-y-4 p-5">
            {fields.length === 0 ? (
              <p className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600">No user preferences are available for this employee.</p>
            ) : fields.map((field) => (
              <label key={field.path.join(".")} className="block text-sm font-medium text-[var(--color-text-primary)]">
                {labelFor(field.key)}
                {optionsByField[String(field.key).toLowerCase()] ? (
                  <select className={fieldClass} value={field.value ?? ""} onChange={(event) => updateField(field, event)}>
                    {optionsByField[String(field.key).toLowerCase()].map((option) => (
                      <option key={option} value={option}>{labelFor(option)}</option>
                    ))}
                  </select>
                ) : typeof field.value === "boolean" ? (
                  <span className="mt-2 flex items-center gap-3 font-normal text-[var(--color-text-secondary)]">
                    <input type="checkbox" checked={field.value} onChange={(event) => updateField(field, event)} className="h-4 w-4 accent-[var(--color-primary)]" />
                    {field.value ? "Enabled" : "Disabled"}
                  </span>
                ) : Array.isArray(field.value) ? (
                  <textarea className={fieldClass} rows={3} defaultValue={JSON.stringify(field.value, null, 2)} onBlur={(event) => updateField(field, event)} />
                ) : (
                  <input className={fieldClass} type={typeof field.value === "number" ? "number" : "text"} value={field.value ?? ""} onChange={(event) => updateField(field, event)} />
                )}
              </label>
            ))}

            {message && <p role="status" className={`rounded-lg px-3 py-2 text-sm ${message.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{message.text}</p>}

            <div className="flex justify-end gap-3 border-t border-[var(--color-border)] pt-4">
              <button type="button" onClick={handleChangePassword} className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Change Password</button>
              <DialogPrimitive.Close type="button" className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</DialogPrimitive.Close>
              <button type="submit" disabled={saving || fields.length === 0} className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? "Saving…" : "Save preferences"}
              </button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
